import React from "react";
import styled from "styled-components";
import moment from "moment";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Skeleton } from "antd";
import EstimatedAmount from "./EstimatedAmount";
import { GoalStats } from "src/store/Goal-Based/goal";
import { colors } from "src/theme/colors";
import { line, radii, shade, shadow, space } from "src/theme/ui";

/**
 * Where the plan is likely to get to.
 *
 * The three scenarios the backend returns — pessimistic, targeted and
 * optimistic — drawn so each of them can be read off the plot. The note on
 * the palette below says what that took, and why the original could not.
 *
 * The tooltip carries one figure the chart does not draw: the money paid in
 * by that date. It is in the response, and it is what separates compounding
 * from a pile of deposits — a curve rising steadily looks the same either
 * way — so it earns a row where a fourth mark would not earn the ink.
 */

/**
 * The three scenarios, in the old chart's own three greens and its own
 * mapping of them.
 *
 * What was wrong with that chart was never the colours — it was that all
 * three were painted as translucent areas from a common baseline at one
 * opacity, laid over each other. With overlapping translucent fills the
 * boundary a reader sees at any point is the top of whichever series is
 * highest there, and the darker patches are only where fills coincide, so
 * the middle series — the targeted path, the actual forecast — had no
 * readable edge anywhere on the plot.
 *
 * The fix is not fewer scenarios, it is opaque fills drawn widest first.
 * Optimistic goes down from zero, then targeted over it, then pessimistic
 * over that, so what a reader ends up looking at is three solid bands whose
 * three boundaries ARE the three values:
 *
 *   ground to the first edge   the pessimistic case
 *   the band above it          what it adds to reach the targeted case
 *   the top band               what it adds again to reach the optimistic
 *
 * Nothing is inferred from a blend, and every edge is a number. The ramp
 * reads the right way round on its own, too: the higher the band the better
 * the scenario, and the greens darken as they climb.
 *
 * The targeted edge is drawn as a line as well, because it is the one of the
 * three a reader plans against and three bands of equal weight leave them to
 * work out which. That line is a DARKENED TARGETED GREEN rather than a
 * neutral: black would have made the middle series the only one of three
 * speaking a different colour language, so the legend read as a swatch, a
 * rule, and a swatch. Same hue, further down its own ramp — which is also
 * what lets it be seen against the targeted band below it and the optimistic
 * band above, where the undarkened colour would vanish into one of them.
 */
const OPTIMISTIC = "#4CAF50";
const TARGETED = "#8BC34A";
const PESSIMISTIC = "#CDDC39";
const TARGETED_EDGE = shade(TARGETED, 0.42);

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.md}px;
`;

/* Dimmed while it is stale, so a reader is never reading an old number as a
   current one — and it comes back at full strength as the new values move
   into place. */
const Plot = styled.div<{ $busy?: boolean }>`
  opacity: ${({ $busy }) => ($busy ? 0.55 : 1)};
  transition: opacity 0.2s ease;

  height: 220px;

  .recharts-cartesian-axis-tick text {
    font-size: 10.5px;
    font-variant-numeric: tabular-nums;
    fill: ${colors.gray[500]};
  }
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 14px;
`;

const SWATCH: Record<string, string> = {
  optimistic: OPTIMISTIC,
  targeted: TARGETED,
  pessimistic: PESSIMISTIC,
  paid: colors.gray[500],
};

/* Three swatches, one language. The targeted key carries the darker edge as
   a border so it names the line as well as the band. */

const Key = styled.span<{
  $kind: "optimistic" | "targeted" | "pessimistic" | "paid";
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${colors.gray[600]};

  /* A band's key is a block of its fill; the paid-in key is a rule, because
     that is what it is on the chart. A swatch that lied about the mark's
     shape would be the legend disagreeing with the picture. */
  &::before {
    content: "";
    width: 12px;
    height: ${({ $kind }) => ($kind === "paid" ? "0" : "10px")};
    border-radius: ${({ $kind }) => ($kind === "paid" ? "0" : "3px")};
    background: ${({ $kind }) =>
      $kind === "paid" ? "transparent" : SWATCH[$kind]};
    border: ${({ $kind }) =>
      $kind === "paid"
        ? `1px dashed ${colors.gray[500]}`
        : $kind === "targeted"
        ? `1.5px solid ${TARGETED_EDGE}`
        : "0"};
  }
`;

const TooltipCard = styled.div`
  min-width: 150px;
  padding: 9px 11px;
  border: 1px solid ${line.hairline};
  border-radius: ${radii.control}px;
  background: ${colors.white};
  box-shadow: ${shadow.pop};
  pointer-events: none;
`;

const TooltipDate = styled.div`
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${colors.gray[500]};
  margin-bottom: 5px;
`;

const Swatch = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  flex: none;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;

/* The paid-in mark, in the tooltip. Its three neighbours carry a block of
   their band's fill; this one is a dashed rule, because that is what it is on
   the chart — and without any mark at all its row read as a footnote to the
   three above it rather than as the fourth thing the chart draws. */
const DashSwatch = styled.span`
  width: 10px;
  height: 0;
  flex: none;
  border-top: 1.5px dashed ${colors.gray[500]};
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  font-size: 12px;
  color: ${colors.gray[600]};

  & + & {
    margin-top: 2px;
  }

  b {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${colors.gray[900]};
  }
`;

/* Whole dollars, grouped, and compact on the axis — a projection over thirty
   years reaches seven figures, and "1,240,000" six times down an axis is a
   wall of digits where "$1.2M" is the reading. */
const compact = Intl.NumberFormat("en", {
  notation: "compact",
  maximumFractionDigits: 1,
}).format;

const money = (value: number) =>
  Number.isFinite(value)
    ? `$${Math.round(value).toLocaleString("en-US")}`
    : "—";

/** Which of the four figures this mode works out. */
export type Solves = "contribution" | "goal" | "initial" | "duration";

export function AmountPredictionChart(props: {
  data: GoalStats | undefined;
  isLoading: boolean;
  /**
   * What to read off the response, named explicitly.
   *
   * It used to be inferred by comparing the DISPLAY TITLE against string
   * literals — "Goal Contribution", "Final / Goal Amount" — so renaming a
   * label anywhere on the form silently fell through to the duration branch
   * and the card showed a span of years where a sum of money belonged. The
   * same failure the landing page's mode switch had.
   */
  solves: Solves;
  label: string;
}) {
  const { data, isLoading, solves, label } = props;

  /**
   * One row per period, with the range as a pair.
   *
   * Recharts draws an area between two values when the point's value IS the
   * pair, which is what makes the band one mark rather than two areas with
   * one of them painted the colour of the page.
   */
  /**
   * Whether a data change should be animated.
   *
   * False on the very first paint and true from then on, so the chart
   * ARRIVES instantly and only MOVES when a value changes. Those are two
   * different animations wearing one flag in Recharts: an entrance sweep
   * delays the first reading and shows a curve that stops where it does not
   * stop, while a transition between two datasets is the opposite — it is
   * the only thing that lets a reader see WHICH part of the projection their
   * edit moved, instead of one picture being replaced by another.
   */
  const [animate, setAnimate] = React.useState(false);
  React.useEffect(() => setAnimate(true), []);

  const series = React.useMemo(() => {
    /**
     * The money the reader has put in, all of it.
     *
     * "Contribution cumulative" is what the backend calls the running total
     * of the CONTRIBUTIONS — it does not include the lump sum paid at the
     * start, so the tooltip's "Paid in" was understating what the plan had
     * cost by the whole initial amount. On a plan opened with ten thousand
     * and fed a hundred a month, the figure the reader compares their
     * projection against was ten thousand short for the plan's entire life.
     *
     * The initial amount is on the same response, so this is a sum of two
     * figures the backend already provides rather than a new derivation.
     */
    const initial = Number(data?.initial_amount ?? 0);

    return (data?.data ?? []).map((row: any) => {
      const low = Number(row["Pessimistic scenario"]);
      const high = Number(row["Optimistic scenario"]);
      const contributed = Number(row["Contribution cumulative"]);
      return {
        index: row.index,
        date: row.date,
        low,
        high,
        targeted: Number(row.investment),
        paid:
          (Number.isFinite(contributed) ? contributed : 0) +
          (Number.isFinite(initial) ? initial : 0),
      };
    });
  }, [data]);

  /**
   * The last projection that arrived, kept while the next one is computed.
   *
   * Every edit to a slider or a date is a new query key, so RTK hands back
   * `undefined` until the answer comes — which is why this card used to
   * replace itself with a skeleton on every keystroke. The chart went away
   * and came back several times while a reader dragged one slider, and the
   * thing they were trying to watch was the thing that kept disappearing.
   *
   * Holding the previous series means the plot stays on screen, dimmed and
   * marked as recalculating, and then MOVES to the new numbers.
   */
  const [shown, setShown] = React.useState(series);
  React.useEffect(() => {
    if (series.length > 0) setShown(series);
  }, [series]);

  /* Nothing has ever arrived: there is no previous picture to keep, so this
     is the one case that still gets a placeholder. */
  const firstLoad = isLoading && shown.length === 0;
  /* A refresh over a projection that is already on screen. */
  const busy = isLoading && shown.length > 0;

  /* One set of motion props for every mark, so the three bands and the line
     move together rather than at three different speeds. Short: this is a
     value changing, not an entrance. */
  const motion = {
    isAnimationActive: animate,
    animationDuration: 420,
    animationEasing: "ease-out" as const,
  };

  /* Years once the plan is long enough that months would repeat the same
     label twelve times over. */
  const spanMonths = shown.length;
  const axisDate = (value: any) => {
    const at = moment(value);
    if (!at.isValid()) return String(value);
    return spanMonths > 36 ? at.format("YYYY") : at.format("MMM YY");
  };

  const amount = React.useMemo(() => {
    if (!data || data?.length === 0) return 0;
    if (solves === "contribution") return Math.round(data?.contribution_amount);
    if (solves === "goal") return Math.round(data?.goal_amount);
    if (solves === "initial") return Math.round(data?.initial_amount);

    const start = new Date(data?.start_date);
    const end = new Date(data?.end_date);
    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    if (months < 0) {
      years -= 1;
      months += 12;
    }
    return { years, months };
  }, [data, solves]);

  return (
    <Wrap>
      <EstimatedAmount amount={amount} titles={{ title: label }} busy={busy} />

      {firstLoad ? (
        <Skeleton active paragraph={{ rows: 4 }} title={false} />
      ) : shown.length === 0 ? null : (
        <>
          <Plot $busy={busy}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={shown}
                margin={{ top: 6, right: 4, bottom: 0, left: 0 }}
              >
                <CartesianGrid
                  vertical={false}
                  stroke={line.soft}
                  strokeDasharray="2 4"
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={axisDate}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={28}
                />
                <YAxis
                  width={52}
                  tickFormatter={(v) => `$${compact(Number(v))}`}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={6}
                />
                <Tooltip
                  cursor={{
                    stroke: colors.gray[300],
                    strokeWidth: 1,
                    strokeDasharray: "3 3",
                  }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const row: any = payload[0].payload;
                    return (
                      <TooltipCard>
                        <TooltipDate>
                          {moment(label).format("MMM YYYY")}
                        </TooltipDate>
                        <TooltipRow>
                          <Swatch $color={OPTIMISTIC} />
                          <span>Optimistic</span>
                          <b>{money(row.high)}</b>
                        </TooltipRow>
                        <TooltipRow>
                          <Swatch $color={TARGETED_EDGE} />
                          <span>Targeted</span>
                          <b>{money(row.targeted)}</b>
                        </TooltipRow>
                        <TooltipRow>
                          <Swatch $color={PESSIMISTIC} />
                          <span>Pessimistic</span>
                          <b>{money(row.low)}</b>
                        </TooltipRow>
                        {/* Not drawn, but it is what separates compounding
                            from a pile of deposits — the contributions plus
                            the lump sum the plan opened with. */}
                        <TooltipRow>
                          <DashSwatch />
                          <span>Paid in</span>
                          <b>{money(row.paid)}</b>
                        </TooltipRow>
                      </TooltipCard>
                    );
                  }}
                />
                {/* Widest first, each one opaque, so the three bands are
                    three readings and not a blend of three washes. */}
                <Area
                  dataKey="high"
                  stroke="none"
                  fill={OPTIMISTIC}
                  fillOpacity={0.9}
                  {...motion}
                />
                <Area
                  dataKey="targeted"
                  stroke="none"
                  fill={TARGETED}
                  fillOpacity={1}
                  {...motion}
                />
                <Area
                  dataKey="low"
                  stroke="none"
                  fill={PESSIMISTIC}
                  fillOpacity={1}
                  {...motion}
                />
                {/* The forecast, given a line of its own. Three bands of
                    equal weight leave a reader to work out which of the three
                    edges is the one to plan against; weight says it without
                    spending another colour. */}
                {/**
                 * The money paid in, drawn.
                 *
                 * It was in the tooltip only, which meant a reader could see
                 * what a plan had cost at one date at a time and never see
                 * the shape of it — and the shape is the point: the distance
                 * between this line and the bands above it IS the growth,
                 * and where they meet is the moment the plan has not made
                 * anything yet.
                 *
                 * Deliberately not a fourth green. The three bands are one
                 * family because they are three readings of the same
                 * forecast; what was paid in is not a forecast at all, it is
                 * a fact, so it is drawn in ink and dashed — the convention
                 * for a reference a reader measures against rather than a
                 * series they are meant to follow.
                 */}
                <Line
                  dataKey="paid"
                  stroke={colors.gray[500]}
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={false}
                  activeDot={{
                    r: 3.5,
                    strokeWidth: 2,
                    stroke: colors.white,
                    fill: colors.gray[600],
                  }}
                  {...motion}
                />
                <Line
                  dataKey="targeted"
                  stroke={TARGETED_EDGE}
                  strokeWidth={1.75}
                  dot={false}
                  activeDot={{
                    r: 4,
                    strokeWidth: 2,
                    stroke: colors.white,
                    fill: TARGETED_EDGE,
                  }}
                  {...motion}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Plot>
          <Legend>
            <Key $kind="optimistic">Optimistic</Key>
            <Key $kind="targeted">Targeted</Key>
            <Key $kind="pessimistic">Pessimistic</Key>
            <Key $kind="paid">Paid in</Key>
          </Legend>
        </>
      )}
    </Wrap>
  );
}
export default AmountPredictionChart;
